import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  SidePanelLoader,
  SidePanelType,
  SidePanel,
  ButtonPrimary,
  ButtonPseudo,
  PermissionEnum,
  Popup,
  PermissionDenied,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearSourceOfFundsFormState } from '../store/slices/source-of-funds-form';
import {
  useLazyGetSourceOfFundsQuery,
  useLazyGetSourceOfFundsListQuery,
  useCreateSourceOfFundsMutation,
  useUpdateSourceOfFundsMutation,
} from '../store/services';
import useRiskScore from '../../helpers/use-risk-score';
import ROUTES from '../../../routes';

interface FormValues {
  name: string;
  riskScoreRangeId: string;
  riskScore: string;
}

const SourceOfFundsForm = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [defaultValues, setDefaultValues] = useState({
    name: '',
    riskScoreRangeId: '',
    riskScore: '',
  });

  const {
    individualClientsRisk: {
      sourceOfFunds: {
        form: { item },
      },
    },
    amlDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const { sourceId } = useParams();
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [getSourceOfFunds, { isLoading, isFetching }] =
    useLazyGetSourceOfFundsQuery();
  const [getList] = useLazyGetSourceOfFundsListQuery();

  const [create, { isLoading: requestInProgress, isSuccess }] =
    useCreateSourceOfFundsMutation();
  const [update, { isLoading: updateInProgress, isSuccess: updateSuccess }] =
    useUpdateSourceOfFundsMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      riskScoreRangeId: '',
      riskScore: '',
    },
  });

  const riskScore = watch('riskScoreRangeId');
  const riskScoreNumber = watch('riskScore');
  const name = watch('name');

  const isDirty = !isEqual(defaultValues, {
    name,
    riskScoreRangeId: riskScore,
    riskScore: riskScoreNumber,
  });

  const { onFormSubmit, form, globalError } = useRiskScore({
    handleSubmit,
    itemId: sourceId,
    setError,
    getList: (query: string) => getList(query),
    update,
    create,
    setValue,
    errors,
    control,
    disabled: Boolean(
      !activePermissions?.includes(
        PermissionEnum.SETTINGS_SOURCES_OF_FUNDS_EDIT_ACTION,
      ) && sourceId,
    ),
  });

  useEffect(() => {
    if (item) {
      reset({
        name: item?.name,
        riskScore: item?.riskScore.toString(),
        riskScoreRangeId: item?.riskScoreRangeId,
      });

      setDefaultValues({
        name: item?.name,
        riskScore: item?.riskScore.toString(),
        riskScoreRangeId: item?.riskScoreRangeId,
      });
    }
  }, [reset, item]);

  const getSourceOfFundsAction = useCallback(() => {
    getSourceOfFunds(sourceId as string);
  }, [sourceId]);

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(
        `${ROUTES.INDIVIDUAL_CLIENTS_RISK_SOURCE_OF_FUNDS}${search || ''}`,
      );
    }, 100);
  }, []);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (updateSuccess) {
      setDefaultValues({
        name,
        riskScoreRangeId: riskScore,
        riskScore: riskScoreNumber,
      });
      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (sourceId) {
      getSourceOfFundsAction();
    }

    return () => {
      dispatch(clearSourceOfFundsFormState());
    };
  }, [dispatch, sourceId, getSourceOfFundsAction]);

  let content = form;
  let buttonText;
  let title;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (
    !isLoading &&
    !sourceId &&
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_SOURCES_OF_FUNDS_CREATE_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  }

  const isEditable = sourceId
    ? activePermissions?.includes(
        PermissionEnum.SETTINGS_SOURCES_OF_FUNDS_EDIT_ACTION,
      )
    : activePermissions?.includes(
        PermissionEnum.SETTINGS_SOURCES_OF_FUNDS_CREATE_ACTION,
      );

  if (sourceId) {
    buttonText = 'Edit source';
  } else {
    buttonText = 'Add source';
  }

  if (sourceId) {
    title = 'Edit source';

    if (!isEditable) {
      title = 'Source info';
    }
  } else {
    title = 'Add source';
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={title}
        errorSidepanel={globalError}
        sidepanelExpandedKey="source-of-funds"
        footer={
          <>
            {isEditable && (
              <ButtonPrimary
                text={
                  requestInProgress || updateInProgress
                    ? 'Loading...'
                    : buttonText
                }
                onClick={onFormSubmit}
                disabled={!riskScore || !name}
                bold
              />
            )}

            <ButtonPseudo
              text="Cancel"
              className="button-white"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default SourceOfFundsForm;
